// src/Pages/CouponAssignment/UploadOfferCodesModal.jsx

import React, { useEffect, useState } from "react";
import {
    Modal,
    Form,
    Select,
    Input,
    DatePicker,
    Upload,
    Button,
    Alert,
    Typography,
    Collapse,
    Result,
    Descriptions,
} from "antd";
import { InboxOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { getAllCoupons, uploadOfferCodes } from "../../Services/coupon";
import { getAllPlan } from "../../Services/subscription";

const { Text } = Typography;

// What the admin has to have done in App Store Connect before this upload can
// work. Kept collapsed - it matters on the first upload of an offer and is
// noise on every one after that.
const AppStoreConnectHelp = () => (
    <Collapse
        ghost
        size="small"
        style={{ marginBottom: 8 }}
        items={[
            {
                key: "help",
                label: (
                    <Text type="secondary">
                        <QuestionCircleOutlined /> Where do these codes come from?
                    </Text>
                ),
                children: (
                    <ol style={{ paddingLeft: 18, margin: 0, fontSize: 13, color: "#595959" }}>
                        <li>
                            App Store Connect &rarr; the subscription (e.g. Travezy Monthly) &rarr;
                            Subscription Pricing &rarr; Offer Codes.
                        </li>
                        <li>
                            Create an offer. Its <Text code>Reference Name</Text> must match what you
                            type below, exactly.
                        </li>
                        <li>Generate one-time codes and download the CSV.</li>
                        <li>Upload that CSV here with the same reference name and Apple's expiry.</li>
                    </ol>
                ),
            },
        ]}
    />
);

const UploadOfferCodesModal = ({ isModalVisible, handleCancel, presetPool = null }) => {
    const [form] = Form.useForm();
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // Set once the upload succeeds - the modal then shows what actually landed
    // instead of a bare "success", because "98 of 100 inserted" is the part the
    // admin needs to see.
    const [result, setResult] = useState(null);

    // Coupon + plan dropdowns. Only used to build the (coupon, plan) pair the
    // codes attach to; the backend rejects a pair that has no offer row.
    const { data: couponData } = useQuery({
        queryKey: ["coupons-for-offer-upload"],
        queryFn: () => getAllCoupons({ limit: 100 }),
        enabled: isModalVisible,
        refetchOnWindowFocus: false,
        retry: false,
    });
    const coupons = couponData?.coupons || [];

    const { data: planData } = useQuery({
        queryKey: ["plans-for-offer-upload"],
        queryFn: () => getAllPlan({ limit: 100 }),
        enabled: isModalVisible,
        refetchOnWindowFocus: false,
        retry: false,
    });
    const plans = planData?.plans || [];

    // Uploading against an existing pool row pre-fills the pair and - most
    // importantly - the reference name, so a new batch for a known offer cannot
    // be typed in slightly differently and silently detach from the coupon.
    useEffect(() => {
        if (!isModalVisible) return;
        setResult(null);
        setFile(null);
        form.resetFields();
        if (presetPool) {
            form.setFieldsValue({
                coupon_id: presetPool.coupon_id,
                plan_id: presetPool.plan_id,
                ios_offer_reference_name: presetPool.ios_offer_reference_name,
            });
        }
    }, [isModalVisible, presetPool, form]);

    const handleSubmit = async () => {
        const values = await form.validateFields();
        setSubmitting(true);
        try {
            const res = await uploadOfferCodes({
                coupon_id: values.coupon_id,
                plan_id: values.plan_id,
                ios_offer_reference_name: values.ios_offer_reference_name?.trim(),
                expires_at: values.expires_at?.toISOString() || null,
                file,
            });
            // A null result means the service already surfaced the API's own
            // message (404 unconfigured pair / 409 duplicate reference name /
            // 400 unusable file), so the form stays open with its values intact
            // for the admin to correct.
            if (res) setResult(res);
        } finally {
            setSubmitting(false);
        }
    };

    const closeAll = () => {
        setResult(null);
        setFile(null);
        form.resetFields();
        handleCancel();
    };

    return (
        <Modal
            title="Upload iOS Offer Codes"
            open={isModalVisible}
            onCancel={closeAll}
            width={620}
            maskClosable={false}
            footer={
                result
                    ? [
                          <Button key="done" type="primary" onClick={closeAll}>
                              Done
                          </Button>,
                      ]
                    : [
                          <Button key="cancel" onClick={closeAll}>
                              Cancel
                          </Button>,
                          <Button
                              key="upload"
                              type="primary"
                              loading={submitting}
                              disabled={!file}
                              onClick={handleSubmit}
                          >
                              Upload
                          </Button>,
                      ]
            }
        >
            {result ? (
                <Result
                    status="success"
                    title={`${result.inserted} code${result.inserted === 1 ? "" : "s"} added.`}
                    subTitle={
                        `${result.skipped_duplicates} were already in the pool. ` +
                        `${result.available_now} available now.`
                    }
                >
                    <Descriptions column={1} size="small" bordered>
                        <Descriptions.Item label="Reference Name">
                            <Text code>{result.ios_offer_reference_name}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Codes in file">{result.codes_in_file}</Descriptions.Item>
                        <Descriptions.Item label="Inserted">{result.inserted}</Descriptions.Item>
                        <Descriptions.Item label="Skipped (already in pool)">
                            {result.skipped_duplicates}
                        </Descriptions.Item>
                        <Descriptions.Item label="Unusable lines">{result.unusable_lines}</Descriptions.Item>
                        <Descriptions.Item label="Available now">{result.available_now}</Descriptions.Item>
                        <Descriptions.Item label="Batch">
                            <Text code>{result.batch_id}</Text>
                        </Descriptions.Item>
                    </Descriptions>
                </Result>
            ) : (
                <>
                    <AppStoreConnectHelp />

                    <Form form={form} layout="vertical" requiredMark="optional">
                        <Form.Item
                            name="coupon_id"
                            label="Coupon"
                            rules={[{ required: true, message: "Select the coupon these codes belong to" }]}
                        >
                            <Select
                                showSearch
                                optionFilterProp="label"
                                placeholder="Select coupon"
                                options={coupons.map((c) => ({ label: c.code, value: c.id }))}
                            />
                        </Form.Item>

                        <Form.Item
                            name="plan_id"
                            label="Plan"
                            rules={[{ required: true, message: "Select the plan these codes unlock" }]}
                        >
                            <Select
                                showSearch
                                optionFilterProp="label"
                                placeholder="Select plan"
                                options={plans.map((p) => ({ label: p.name, value: p.id }))}
                            />
                        </Form.Item>

                        <Form.Item
                            name="ios_offer_reference_name"
                            label="App Store Connect Reference Name"
                            extra="Must match the Reference Name in App Store Connect exactly."
                            rules={[
                                { required: true, message: "An App Store Connect reference name is required" },
                            ]}
                        >
                            <Input placeholder="welcome30_monthly" style={{ fontFamily: "monospace" }} />
                        </Form.Item>

                        <Form.Item
                            name="expires_at"
                            label="Expires At"
                            extra="Apple one-time codes stop working six months after they are generated. Leave blank only if this batch genuinely has no expiry."
                        >
                            <DatePicker showTime style={{ width: "100%" }} />
                        </Form.Item>

                        <Form.Item
                            label="Offer Code CSV"
                            required
                            extra="Apple's export, one code per line. A header row and blank lines are skipped automatically."
                        >
                            <Upload.Dragger
                                accept=".csv,.txt"
                                maxCount={1}
                                // Held in state and sent by our own service call
                                // instead of antd's uploader, so the request
                                // carries the auth token interceptor.
                                beforeUpload={(f) => {
                                    setFile(f);
                                    return false;
                                }}
                                onRemove={() => setFile(null)}
                                fileList={file ? [{ uid: "1", name: file.name }] : []}
                            >
                                <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text" style={{ fontSize: 13 }}>
                                    Click or drag the CSV here
                                </p>
                            </Upload.Dragger>
                        </Form.Item>
                    </Form>

                    <Alert
                        type="info"
                        showIcon
                        message="Re-uploading the same file is safe"
                        description="Codes already in the pool are skipped, not duplicated - a partially-failed import can simply be uploaded again."
                    />
                </>
            )}
        </Modal>
    );
};

export default UploadOfferCodesModal;
